import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSyncedScroll } from './useSyncedScroll';

interface UseGhostHeaderOptions {
  enabled: boolean;
  hasData: boolean;
  columnOrder: string[];
  isTAccountViewEnabled: boolean;
}

export function useGhostHeader({ enabled, hasData, columnOrder, isTAccountViewEnabled }: UseGhostHeaderOptions) {
  const realTableRef = useRef<HTMLTableElement>(null);
  const ghostScrollRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const stickyScrollbarRef = useRef<HTMLDivElement>(null);
  const { syncScroll } = useSyncedScroll();

  const [realTableNode, setRealTableNode] = useState<HTMLTableElement | null>(null);
  const [contentContainerNode, setContentContainerNode] = useState<HTMLDivElement | null>(null);

  const setRealTableRef = useCallback((node: HTMLTableElement | null) => {
    realTableRef.current = node;
    setRealTableNode(node);
  }, []);
  const setContentContainerRef = useCallback((node: HTMLDivElement | null) => {
    contentContainerRef.current = node;
    setContentContainerNode(node);
  }, []);

  const [ghostVisible, setGhostVisible] = useState(false);
  const [colWidths, setColWidths] = useState<number[]>([]);
  const [realTableWidth, setRealTableWidth] = useState(0);
  const [ghostLeft, setGhostLeft] = useState(0);
  const [ghostWidth, setGhostWidth] = useState(0);

  // Watch real thead intersection to toggle ghost visibility.
  // Ghost shows only while the thead has scrolled above the viewport but the table
  // body is still at least partially in view. Two observers are required:
  //   - theadObserver: fires when thead enters/leaves the viewport
  //   - tableObserver: fires when the full table enters/leaves the viewport
  // Ghost = thead out of view AND table still in view.
  useEffect(() => {
    if (!enabled || !realTableNode) return;
    const table = realTableNode;
    const thead = table.querySelector('thead');
    if (!thead) return;

    const TOP_OFFSET = 30;
    // A collapsed ExpandableHandler ancestor (@entur/expand's CSS-grid `0fr` + `overflow:
    // hidden` collapse) keeps this table mounted with its own natural, non-zero
    // getBoundingClientRect() — only its PAINTED area is clipped away by the ancestor, so a
    // plain rect check can't tell "collapsed" from "genuinely visible." `isIntersecting` alone
    // isn't reliable either: the spec sets intersectionRatio (and isIntersecting) to 1 for a
    // zero-area target that nominally overlaps the root, exactly our collapsed-table case — so
    // this also requires the reported intersectionRect to have real width and height.
    let tableIntersecting = false;
    const update = () => {
      if (!tableIntersecting) {
        setGhostVisible(false);
        return;
      }
      const headRect = thead.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();
      setGhostVisible(headRect.bottom < TOP_OFFSET && tableRect.bottom > 0);
    };

    const theadObserver = new IntersectionObserver(() => update(), {
      threshold: 0,
      rootMargin: `-${TOP_OFFSET}px 0px 0px 0px`,
    });
    const tableObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        tableIntersecting = Boolean(
          entry?.isIntersecting && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0
        );
        update();
      },
      { threshold: 0 }
    );

    theadObserver.observe(thead);
    tableObserver.observe(table);
    window.addEventListener('scroll', update, { passive: true });
    update();

    return () => {
      theadObserver.disconnect();
      tableObserver.disconnect();
      window.removeEventListener('scroll', update);
      setGhostVisible(false);
    };
  }, [enabled, hasData, isTAccountViewEnabled, realTableNode]);

  // Sync scroll position after the ghost renders visible (display:none→block can reset scrollLeft)
  useEffect(() => {
    if (ghostVisible && ghostScrollRef.current && contentContainerRef.current) {
      ghostScrollRef.current.scrollLeft = contentContainerRef.current.scrollLeft;
    }
  }, [ghostVisible]);

  // Sync column widths from real table to ghost colgroup.
  // ResizeObserver handles data-driven width changes; columnOrder re-runs
  // the effect when the header structure changes due to column reordering.
  useEffect(() => {
    if (!enabled || !realTableNode) return;
    const syncWidths = () => {
      const ths = Array.from(realTableNode.querySelectorAll('thead tr:first-child th'));
      setColWidths(ths.map((th) => th.getBoundingClientRect().width));
      setRealTableWidth(realTableNode.scrollWidth);
    };
    syncWidths();
    const observer = new ResizeObserver(syncWidths);
    observer.observe(realTableNode);
    return () => observer.disconnect();
  }, [enabled, hasData, columnOrder, isTAccountViewEnabled, realTableNode]);

  // Track container position for position:fixed placement
  useEffect(() => {
    if (!enabled || !contentContainerNode) return;
    const update = () => {
      const rect = contentContainerNode.getBoundingClientRect();
      setGhostLeft(rect.left);
      setGhostWidth(rect.width);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(contentContainerNode);
    return () => observer.disconnect();
  }, [enabled, hasData, isTAccountViewEnabled, contentContainerNode]);

  const handleTableContainerScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) =>
      syncScroll((e.target as HTMLDivElement).scrollLeft, [ghostScrollRef, stickyScrollbarRef]),
    [syncScroll]
  );

  const handleStickyScrollbarScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) =>
      syncScroll((e.target as HTMLDivElement).scrollLeft, [contentContainerRef, ghostScrollRef]),
    [syncScroll]
  );

  return {
    realTableRef: setRealTableRef,
    ghostScrollRef,
    contentContainerRef: setContentContainerRef,
    stickyScrollbarRef,
    ghostVisible,
    colWidths,
    realTableWidth,
    ghostLeft,
    ghostWidth,
    handleTableContainerScroll,
    handleStickyScrollbarScroll,
  };
}
